import { sanitizeEntityObjectiveLabelCode, getCapitalizedSingular, getCapitalizedPlural } from 'helpers/labelCode';
import { get } from 'lodash';
import { DELIVERY_PLATFORM_DCM, DELIVERY_PLATFORM_TTD } from 'constants/deliveryPlatforms';
import { objectiveDisplayNameMap, programmaticObjectiveNakedToken } from 'constants/programmaticObjectives';
import { convertToDisplayValue } from 'helpers/strings';
import { setObjectiveShadow } from 'services/config/duck';
import { getObjectiveShadow } from 'services/config/selectors';


exports.getObjectiveDisplayName = key => objectiveDisplayNameMap[key] || key;

exports.getActivitiesSettings = (entityObjective, entityObjectiveLabelCode = '') => {
    const labelCode = sanitizeEntityObjectiveLabelCode(entityObjectiveLabelCode);

    const singularTerm = entityObjective !== 'ecpa' ? 'Click' : getCapitalizedSingular(labelCode);
    const pluralTerm = entityObjective !== 'ecpa' ? 'Clicks' : getCapitalizedPlural(labelCode);

    // generic case
    const labelTerms = {
        isLabelCoded: true,
        singularTerm,
        pluralTerm,
        calloutTitle: pluralTerm,
        heatmapTerm: pluralTerm.toUpperCase(),
        fullObjectiveTerm: pluralTerm,
        helpKeySuffix: `_labelCode_${pluralTerm.toLowerCase()}`,
        campaignActivitiesOrClicks: 'campaign_activities',
        campaignActivitiesOrClicksTerm: `Total ${pluralTerm}`,
        eCPAActivitiesOrClicks: 'ecpa_activities',
        eCPAActivitiesOrClicksTerm: pluralTerm,
        conversionTermSingular: singularTerm,
        conversionTermPlural: pluralTerm,
        ecpaTerm: `Cost per ${singularTerm}`,
        primaryActivityField: `${entityObjective}_activities`,
        primaryActivityFieldTerm: pluralTerm,
        showActivitiesData: true
    };

    // If objective eKPI or CTR
    if (entityObjective !== 'ecpa') {
        Object.assign(labelTerms, {
            isLabelCoded: false,
            calloutTitle: 'Total Clicks',
            fullObjectiveTerm: `${exports.getObjectiveDisplayName(entityObjective)} ${pluralTerm}`,
            helpKeySuffix: '',
            campaignActivitiesOrClicks: 'clicks',
            campaignActivitiesOrClicksTerm: 'Total Clicks',
            eCPAActivitiesOrClicks: 'clicks',
            ecpaTerm: 'eCPA',
            conversionTermSingular: 'Conversion',
            conversionTermPlural: 'Conversions',
            primaryActivityField: entityObjective === 'ctr' ? 'total_activities' : `${entityObjective}_activities`,
            primaryActivityFieldTerm: convertToDisplayValue('total_activities'),
            showActivitiesData: false
        });
        // else if prog type is eCPA but NO label is set (default)
    } else if (!labelCode || labelCode === '') {
        Object.assign(labelTerms, {
            isLabelCoded: false,
            singularTerm: 'Activity',
            pluralTerm: 'Activities',
            calloutTitle: 'eCPA Activities',
            heatmapTerm: 'ACTIVITIES',
            fullObjectiveTerm: 'eCPA Activities',
            helpKeySuffix: '',
            campaignActivitiesOrClicks: 'campaign_activities',
            campaignActivitiesOrClicksTerm: 'Campaign Activities',
            eCPAActivitiesOrClicks: 'ecpa_activities',
            eCPAActivitiesOrClicksTerm: 'eCPA Activities',
            primaryActivityFieldTerm: 'eCPA Activities',
            ecpaTerm: 'eCPA Activities',
            conversionTermSingular: 'Conversion',
            conversionTermPlural: 'Conversions'
        });
    }

    return labelTerms;
};

exports.getAudioObjectiveSettings = (
    deliveryPlatform,
    entityObjective,
    eCPAActivitiesOrClicks,
    calloutTitle,
    helpKeySuffix
) => {
    // default case is ttd and ecpa
    const settings = {
        creativeColumns: ['label', 'clicks', 'ecpa_activities', 'startedPlays', 'completed_plays', 'completion_rate'],
        placementsColumns: ['placement', 'impressions', 'clicks', 'ecpa_activities', 'completion_rate', 'spend'],
        targetingTacticsColumns: ['targetingTactic', 'impressions', 'clicks', 'ecpa_activities', 'completion_rate'],
        summaryColumns: ['impressions', 'ecpa_activities', 'completion_rate', 'spend'],
        placementsDefaultSorted: [
            {
                id: 'completion_rate',
                desc: true
            }
        ],
        showCompletionRate: true,
        calloutSettings: [
            { key: 'impressions', title: 'Impressions', format: '0,0' },
            { key: eCPAActivitiesOrClicks, title: calloutTitle, helpKey: `ecpa${helpKeySuffix || ''}`, format: '0,0' },
            { key: 'completion_rate', title: 'Completion Rate', format: '0.00%' }
        ]
    };

    // Data source can be ttd or dcm
    // Objective can be ecpa or ctr
    if (deliveryPlatform === DELIVERY_PLATFORM_DCM && entityObjective === 'ecpa') {
        settings.creativeColumns = ['label', 'impressions', 'clicks', 'ecpa_activities'];
        settings.placementsColumns = ['placement', 'impressions', 'clicks', 'ecpa_activities', 'spend'];
        settings.targetingTacticsColumns = ['targetingTactic', 'impressions', 'clicks', 'ecpa_activities'];
        settings.summaryColumns = ['impressions', 'ecpa_activities', 'spend'];
        settings.showCompletionRate = false;
        settings.placementsDefaultSorted = [
            {
                id: 'impressions',
                desc: true
            }
        ];
        settings.calloutSettings.pop();
    } else if (deliveryPlatform === DELIVERY_PLATFORM_DCM && entityObjective === 'ctr') {
        settings.creativeColumns = ['label', 'impressions', 'clicks', 'ctr'];
        settings.placementsColumns = ['placement', 'impressions', 'clicks', 'ctr', 'spend'];
        settings.targetingTacticsColumns = ['targetingTactic', 'impressions', 'clicks', 'ctr'];
        settings.summaryColumns = ['impressions', 'clicks', 'ctr', 'spend'];
        settings.showCompletionRate = false;
        settings.placementsDefaultSorted = [
            {
                id: 'impressions',
                desc: true
            }
        ];
        settings.calloutSettings.pop();
        settings.calloutSettings.push({ key: 'ctr', title: 'CTR', format: '0.00%' });
    } else if (deliveryPlatform === DELIVERY_PLATFORM_TTD && entityObjective === 'ctr') {
        settings.creativeColumns = ['label', 'clicks', 'ctr', 'startedPlays', 'completed_plays', 'completion_rate'];
        settings.placementsColumns = ['placement', 'impressions', 'clicks', 'ctr', 'completion_rate', 'spend'];
        settings.targetingTacticsColumns = ['targetingTactic', 'impressions', 'clicks', 'ctr', 'completion_rate'];
        settings.summaryColumns = ['impressions', 'clicks', 'ctr', 'completion_rate', 'spend'];
        settings.showCompletionRate = true;
        settings.placementsDefaultSorted = [
            {
                id: 'impressions',
                desc: true
            }
        ];
        settings.calloutSettings.pop();
        settings.calloutSettings.push({ key: 'ctr', title: 'CTR', format: '0.00%' });
    }

    return settings;
};

exports.getQueryParamDefForObjectiveSwitcher = () => ({
    [programmaticObjectiveNakedToken]: {
        selector: state => (get(state, 'entities.meta.meta.enableObjectiveSwitching', false) ? getObjectiveShadow(state) : get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa')),
        action: (value) => {
            if (['ecpa', 'ctr'].includes(value)) {
                return setObjectiveShadow(value);
            }
            return { type: 'NOOP', payload: 'ObjectiveSwitcher' };
        },
        defaultValue: '', // this hides the parameter when not present
    }
});



// WEBPACK FOOTER //
// ./src/helpers/objectiveSettings.js